import { useEffect, useState } from 'react';
import { css, keyframes, Keyframes } from 'styled-components';

export const HIGHLIGHT_GOLD = '#f2c14d';
export const LENS_GOLD = '#e2bb5d';
export const LENS_GREEN = '#003d29';
export const GREEN_TRANSPARENT = 'rgba(0, 61, 41, 0)';

// Masks out the center of a shape, leaving only a thin ring visible along its
// edge — used to clip a rotating/gradient background into a border-hugging highlight.
export const ringMaskCss = css`
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
`;

export const SVG_PAD = 12;
export const LENS_LENGTH = 22;

const travelFrom = (adjust: number): Keyframes => keyframes`
  from { stroke-dashoffset: ${adjust}; }
  to { stroke-dashoffset: ${adjust - 100}; }
`;

export const baseTravel = travelFrom(0);

export const colorPulse = keyframes`
  0%, 25% { stroke: ${LENS_GOLD}; }
  50%, 75% { stroke: ${LENS_GREEN}; }
  100%  { stroke: ${LENS_GOLD}; }
`;

export const spin = keyframes`
  from { transform: translate(-50%, -50%) rotate(0deg); }
  to   { transform: translate(-50%, -50%) rotate(360deg); }
`;

const LENS_TAPER_EXPONENT = 0.75;

export type TaperedLayer = { width: number; length: number; alpha: number; travel: Keyframes };

const buildTaperedLayers = (count: number, maxWidth: number, minWidth: number): TaperedLayer[] => {
  const layers: TaperedLayer[] = [];
  let cumulative = 0;
  for (let i = 0; i < count; i += 1) {
    const length = (LENS_LENGTH * (count - i)) / count;
    const width = Math.max(minWidth, maxWidth * (1 - length / LENS_LENGTH) ** LENS_TAPER_EXPONENT);
    const target = (i + 0.7) / count;
    const alpha = (target - cumulative) / (1 - cumulative);
    cumulative = target;
    layers.push({ width, length, alpha, travel: travelFrom((length - LENS_LENGTH) / 2) });
  }
  return layers;
};

export const lensLayers = buildTaperedLayers(12, 1.5, 0.1);
export const glowLayers = buildTaperedLayers(6, 6, 1.2);

function parseRgb(color: string): [number, number, number] | null {
  const m = /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/.exec(color);
  return m ? [parseInt(m[1], 10), parseInt(m[2], 10), parseInt(m[3], 10)] : null;
}

function srgbChannel(c: number): number {
  const s = c / 255;
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance([r, g, b]: [number, number, number]): number {
  return 0.2126 * srgbChannel(r) + 0.7152 * srgbChannel(g) + 0.0722 * srgbChannel(b);
}

function wcagContrast(l1: number, l2: number): number {
  const [hi, lo] = l1 > l2 ? [l1, l2] : [l2, l1];
  return (hi + 0.05) / (lo + 0.05);
}

export type ButtonMetrics = { w: number; h: number; radius: number };

export function useButtonMetrics(buttonRef: React.RefObject<HTMLElement>): ButtonMetrics | null {
  const [metrics, setMetrics] = useState<ButtonMetrics | null>(null);

  useEffect(() => {
    const el = buttonRef.current;
    if (!el) return undefined;
    const update = (): void =>
      setMetrics({
        w: el.offsetWidth,
        h: el.offsetHeight,
        radius: parseFloat(getComputedStyle(el).borderTopLeftRadius) || 0,
      });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [buttonRef]);

  return metrics;
}

// Darkens the button's background when var(--dcm-color-primary) has insufficient
// contrast (WCAG ratio < 2.5:1) against the nearest opaque ancestor background.
export function useNeedsDarkening(buttonRef: React.RefObject<HTMLElement>): boolean {
  const [needsDarkening, setNeedsDarkening] = useState(false);

  useEffect(() => {
    const el = buttonRef.current;
    if (!el) return;

    const probe = document.createElement('span');
    probe.style.cssText = 'position:absolute;visibility:hidden;background-color:var(--dcm-color-primary)';
    el.appendChild(probe);
    const btnColor = getComputedStyle(probe).backgroundColor;
    el.removeChild(probe);

    let surroundingBg = 'rgb(255,255,255)';
    let ancestor = el.parentElement;
    while (ancestor) {
      const bg = getComputedStyle(ancestor).backgroundColor;
      if (bg !== 'transparent' && bg !== 'rgba(0, 0, 0, 0)') {
        surroundingBg = bg;
        break;
      }
      ancestor = ancestor.parentElement;
    }

    const btnRgb = parseRgb(btnColor);
    const bgRgb = parseRgb(surroundingBg);
    if (btnRgb && bgRgb) {
      const ratio = wcagContrast(relativeLuminance(btnRgb), relativeLuminance(bgRgb));
      setNeedsDarkening(ratio < 2.5);
    }
  }, [buttonRef]);

  return needsDarkening;
}
